import {
  deserialize,
  DocumentObject,
  serialize,
  transform,
  Transformer,
  whitelist,
  DefaultTransformer,
  JsonObject,
} from '@jsonapiworld/jsonapi-serializer';
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import filter from 'lodash/filter';
import isEqual from 'lodash/isEqual';
import {
  approvePaymentBeneficiary,
  cancelPayment,
  checkPaymentBeneficiary,
  fetchLatestPaymentByPayeeIdentifier,
  fetchBulkPaymentImportConfig,
  fetchPayments,
  FetchPaymentsParams,
  PaymentPageSize,
} from '~/api/payments';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { useFilterState } from '~/state/tableFilters';
import { useSorting } from '~/state/tableSorting';
import { AccountResourceType } from '~/types/account';
import { CompanyResourceType } from '~/types/company';
import { CounterpartResourceType } from '~/types/counterparts';
import { FlowResourceType } from '~/types/flow';
import { IbanValidationRequest, IbanValidationResponse } from '~/types/iban-validation';
import { MetaDataCursorPage } from '~/types/jsonapi';
import {
  CreatePayment,
  Payment,
  PaymentFetchParameter,
  PaymentQueryKeys,
  PaymentResourceType,
  PaymentsFiltersFields,
  UpdatePaymentData,
  PaymentsKeys,
  UpdatePaymentStatus,
  PaymentFullDetails,
  PaymentsSortFields,
  PaymentBeneficiaryCheckPayload,
} from '~/types/payment';
import {
  buildJsonAPIQueryParams,
  jsonAPIFieldsBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  SortInfo,
} from '~/utils/jsonapi-query';
import { AnalyticsEvents } from './analytics';
import { backendApiGateway } from '../client/backend';

export class CreatePaymentTransformer extends Transformer<CreatePayment, {}> {
  constructor() {
    super();
    this.type = PaymentResourceType;
    this.relationships = {
      flows: this.fieldRelatioships('flows', FlowResourceType),
      account: this.fieldRelatioship('account', AccountResourceType),
      company: this.fieldRelatioship('company', CompanyResourceType),
      counterpart: this.fieldRelatioship('counterpart', CounterpartResourceType),
      parent: this.fieldRelatioship('parent', PaymentResourceType),
    };
  }

  transform(payload: CreatePayment): JsonObject {
    const relationships = ['flows', 'account', 'company', 'counterpart', 'parent'];
    const keys = filter(Object.keys(payload), (val) => !relationships.includes(val));
    return whitelist(payload, keys);
  }

  fieldRelatioship(field: 'account' | 'company' | 'counterpart' | 'parent', resourceType: string) {
    return function (payload: CreatePayment) {
      return transform()
        .withInput(payload[field] ?? null)
        .withTransformer(new DefaultTransformer(resourceType))
        .toContext();
    };
  }

  fieldRelatioships(field: 'flows', resourceType: string) {
    return function (payload: CreatePayment) {
      return transform().withInput(payload[field]).withTransformer(new DefaultTransformer(resourceType)).toContext();
    };
  }
}

const createPayment = async (values: CreatePayment) => {
  const payload = transform<CreatePayment, {}>()
    .withInput(!values.counterpart?.id ? { ...values, counterpart: undefined } : values)
    .withTransformer(new CreatePaymentTransformer())
    .serialize();

  const includeBuilder = jsonAPIIncludeBuilder(['account', 'company']);
  const fieldsBuilder = jsonAPIFieldsBuilder([
    {
      resource: AccountResourceType,
      fields: [],
    },
  ]);

  const searchParams = buildJsonAPIQueryParams(includeBuilder, fieldsBuilder);

  const { data } = await backendApiGateway.post<DocumentObject>('/api/v1/payments', payload, {
    params: searchParams,
  });

  const deserialized = deserialize<Payment>(data) as Payment;
  return deserialized;
};

export const useCreatePayment = () => {
  const queryClient = useQueryClient();
  const companyId = useActiveCompanyId();
  const { captureException } = useExceptionLogger();

  return useMutation({
    mutationFn: (params: Omit<CreatePayment, 'company'>) => createPayment({ ...params, company: { id: companyId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PaymentQueryKeys.all });
    },
    onError: (error) => {
      captureException(error);
    },
  });
};

const updatePaymentUserData = async ({ paymentId, userData, debug }: UpdatePaymentData): Promise<Payment> => {
  const payload = serialize(
    {
      id: paymentId,
      userData,
      debug,
    },
    PaymentResourceType,
  );

  const includeBuilder = jsonAPIIncludeBuilder(['account', 'company']);
  const fieldsBuilder = jsonAPIFieldsBuilder([
    {
      resource: AccountResourceType,
      fields: [],
    },
  ]);

  const searchParams = buildJsonAPIQueryParams(includeBuilder, fieldsBuilder);

  const { data } = await backendApiGateway.patch<{}, AxiosResponse<DocumentObject>>(
    `/api/v1/payments/${paymentId}`,
    payload,
    { params: searchParams },
  );
  const payment = deserialize(data) as Payment;
  return payment;
};

export const useUpdatePaymentUserData = () =>
  useMutation({
    mutationFn: updatePaymentUserData,
    onSettled: () => queryClient.invalidateQueries({ queryKey: PaymentQueryKeys.all }),
  });

const fetchPayment = async (paymentId: Payment['id']): Promise<Payment> => {
  const includeBuilder = jsonAPIIncludeBuilder(['account', 'flows']);
  const fieldsBuilder = jsonAPIFieldsBuilder([
    {
      resource: AccountResourceType,
      fields: [],
    },
  ]);

  const searchParams = buildJsonAPIQueryParams(includeBuilder, fieldsBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/payments/${paymentId}`, {
    params: searchParams,
  });
  return deserialize<Payment>(data) as Payment;
};

export const usePaymentQuery = (paymentId: Payment['id'] | null) => {
  return useQuery({
    queryKey: PaymentQueryKeys.single(paymentId),
    queryFn: () => fetchPayment(paymentId ?? ''),
    enabled: !!paymentId,
  });
};

export const useLatestPaymentByPayeeIdentifierQuery = ({
  payeeIdentifier,
  enabled = true,
}: {
  payeeIdentifier?: string | null;
  enabled?: boolean;
}) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: PaymentQueryKeys.latestByPayeeIdentifier(companyId, payeeIdentifier ?? ''),
    queryFn: (): Promise<Payment | null> =>
      fetchLatestPaymentByPayeeIdentifier({
        companyId,
        payeeIdentifier: payeeIdentifier!,
      }),
    enabled: enabled && Boolean(companyId) && Boolean(payeeIdentifier),
  });
};

export const useCancelPayment = () => {
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: cancelPayment,
    onSettled: () => queryClient.invalidateQueries({ queryKey: PaymentQueryKeys.all }),
    onError: (error, paymentId) => {
      captureException(error, { extra: { paymentId } });
    },
  });
};

export const usePaymentInfinityQuery = ({ filters }: { filters: Partial<PaymentFetchParameter> }) => {
  const companyId = useActiveCompanyId();
  const fullFilters: PaymentFetchParameter = { companyId, ...filters };
  const sorting = useSorting<PaymentsSortFields>();
  const fullSortedFilters: FetchPaymentsParams = { ...fullFilters, ...sorting };
  return useInfiniteQuery({
    queryKey: PaymentQueryKeys.companyInfinityList(companyId, fullSortedFilters),
    queryFn: ({ pageParam }) => fetchPayments({ pageParam, ...fullSortedFilters }),
    initialPageParam: '',
    getNextPageParam: ({ page: { cursor }, payments }: { page: MetaDataCursorPage; payments: Payment[] }) => {
      if (payments.length >= PaymentPageSize) {
        return cursor;
      }
      return undefined;
    },
  });
};

export const usePaymentsQuery = ({
  filters,
  enabled,
}: {
  filters: Partial<PaymentFetchParameter>;
  enabled: boolean;
}) => {
  const companyId = useActiveCompanyId();
  const fullFilters: PaymentFetchParameter = { companyId, ...filters };
  const sorting = {
    sort: 'requestedExecutionAt',
    direction: 'desc',
  } as SortInfo<PaymentsSortFields>;

  const fullSortedFilters: FetchPaymentsParams = { ...fullFilters, ...sorting };
  return useQuery({
    queryKey: PaymentQueryKeys.list(companyId, fullSortedFilters),
    queryFn: () => fetchPayments({ ...fullSortedFilters }),
    enabled,
  });
};

interface PaymentsMetaData {
  total: number;
}

const fetchPaymentMetadata = async ({ filters }: { filters: PaymentFetchParameter }) => {
  const { search, dateRange, status, companyId } = filters;
  const filterBuilder = jsonAPIFilterBuilder();
  if (companyId) {
    filterBuilder.eq('company.id', companyId);
  }
  if (search) filterBuilder.ilike('search', search);

  if (status) filterBuilder.in('status', status);

  const [startDate, endDate] = dateRange ?? [null, null];
  if (startDate) {
    filterBuilder.gte('requestedExecutionAt', startDate);
  }
  if (endDate) {
    filterBuilder.lt('requestedExecutionAt', endDate);
  }

  const queryParams = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<PaymentsMetaData>('/api/v1/payments/metadata', {
    params: queryParams,
  });

  return data;
};

export const usePaymentsMetadata = (filters: Partial<PaymentFetchParameter>) => {
  const companyId = useActiveCompanyId();
  const fullFilters: PaymentFetchParameter = { companyId, ...filters };

  return useQuery({
    queryKey: PaymentsKeys.metadata(fullFilters),
    queryFn: () => fetchPaymentMetadata({ filters: fullFilters }),
    enabled: !!companyId,
    staleTime: Infinity,
  });
};

const updatePaymentStatus = async ({ paymentId, status }: UpdatePaymentStatus): Promise<Payment> => {
  const { data } = await backendApiGateway.post<{}, AxiosResponse<DocumentObject>>(
    `/api/v1/payments/${paymentId}/change-status`,
    { status },
  );
  const payment = deserialize(data) as Payment;
  return payment;
};

export const useUpdatePaymentStatus = () => {
  const { track } = useAnalytics();
  return useMutation({
    mutationFn: updatePaymentStatus,
    onSuccess: (_data, { status }) => track(AnalyticsEvents.PAYMENT_STATUS_UPDATE_MANUALLY, { status }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: PaymentQueryKeys.all }),
  });
};

const fetchPaymentFullDetails = async (paymentId: Payment['id']): Promise<PaymentFullDetails> => {
  const includeBuilder = jsonAPIIncludeBuilder(['account', 'counterpart', 'company', 'company.companyIdentity']);
  const searchParams = buildJsonAPIQueryParams(includeBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/payments/${paymentId}`, {
    params: searchParams,
  });
  return deserialize<PaymentFullDetails>(data) as PaymentFullDetails;
};

export const usePaymentFullDetailsQuery = (paymentId: Payment['id'] | null) => {
  return useQuery({
    queryKey: PaymentQueryKeys.fullDetails(paymentId),
    queryFn: () => fetchPaymentFullDetails(paymentId ?? ''),
    enabled: !!paymentId,
  });
};

export const useBulkPaymentImportConfig = () => {
  return useMutation({
    mutationFn: fetchBulkPaymentImportConfig,
  });
};

const validateIban = async (request: IbanValidationRequest): Promise<IbanValidationResponse> => {
  const { data } = await backendApiGateway.post<IbanValidationResponse>('/api/v1/payments/validate-iban', request);

  return data;
};

const ibanValidationQuery = (companyId: string | undefined, iban: string, enabled: boolean) => ({
  queryKey: ['iban-validation', companyId, iban],
  queryFn: () => validateIban({ iban }),
  enabled: enabled && !!iban && !!companyId,
});

export const useIbanValidation = (iban: string, enabled = true) => {
  const companyId = useActiveCompanyId();

  return useQuery(ibanValidationQuery(companyId, iban, enabled));
};

export const useIbansValidation = (ibans: string[], enabled = true) => {
  const companyId = useActiveCompanyId();

  return useQueries({
    queries: ibans.map((iban) => ibanValidationQuery(companyId, iban, enabled)),
  });
};

export const useApprovePaymentBeneficiary = () => {
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: approvePaymentBeneficiary,
    onError: (error, data) => {
      captureException(error, { extra: { data } });
    },
  });
};

export const useCheckPaymentBeneficiary = () => {
  const { captureException } = useExceptionLogger();

  return useMutation({
    mutationFn: (payload: PaymentBeneficiaryCheckPayload) => checkPaymentBeneficiary(payload),
    onError: (error, data) => {
      captureException(error, { extra: { data } });
    },
  });
};

interface PaymentCounts {
  totalCount: number;
}

export const usePaymentCounts = (): PaymentCounts => {
  const filters = useFilterState<PaymentsFiltersFields>();
  const { data } = usePaymentsMetadata(filters);
  const totalCount = data?.total ?? 0;
  return { totalCount };
};

export const useHasActiveSearchOrDateFilter = () => {
  const filters = useFilterState<PaymentsFiltersFields>();
  const { status: _status, ...rest } = filters;
  return !isEqual(rest, { search: '', dateRange: [null, null] });
};
